import React from 'react';
import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Typography,
  CircularProgress,
} from '@mui/material';
import LoadingSkeleton from './LoadingSkeleton';
import EmptyState from './EmptyState';
import { Add as AddIcon } from '@mui/icons-material';

export interface Column<T> {
  id: string;
  label: string;
  align?: 'left' | 'center' | 'right';
  minWidth?: number;
  format?: (value: any, row: T) => React.ReactNode;
  /** Hide this column on xs/sm viewports (mobile) */
  hideOnMobile?: boolean;
}

export interface TableProps<T> {
  columns: Column<T>[];
  data?: T[];  // Made optional to handle undefined gracefully
  loading?: boolean;
  page?: number;
  rowsPerPage?: number;
  totalRows?: number;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  rowsPerPageOptions?: number[];
  emptyMessage?: string;
  getRowId?: (row: T, index: number) => string | number;
  onRowClick?: (row: T) => void;
  /** Show action button in empty state */
  onEmptyAction?: () => void;
  /** Label for empty state action button */
  emptyActionLabel?: string;
}

/**
 * Reusable Table component with pagination support
 * Supports Thai language labels and custom formatting
 */
function Table<T extends Record<string, any>>({
  columns,
  data,
  loading = false,
  page = 0,
  rowsPerPage = 10,
  totalRows,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [10, 25, 50, 100],
  emptyMessage = 'ไม่พบข้อมูล',
  getRowId = (row, index) => index,
  onRowClick,
  onEmptyAction,
  emptyActionLabel = 'สร้างใหม่',
}: TableProps<T>) {
  const handleChangePage = (_event: unknown, newPage: number) => {
    if (onPageChange) {
      onPageChange(newPage);
    }
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onRowsPerPageChange) {
      onRowsPerPageChange(parseInt(event.target.value, 10));
    }
  };

  const total = totalRows !== undefined ? totalRows : (data || []).length;

  return (
    <Paper sx={{ width: '100%' }}>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <MuiTable stickyHeader aria-label="ตารางข้อมูล">
          <TableHead>
            <TableRow sx={{ 
              backgroundColor: 'primary.main',
              '& th:first-child': {
                borderTopLeftRadius: 12,
              },
              '& th:last-child': {
                borderTopRightRadius: 12,
              },
            }}>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align || 'left'}
                  style={{ minWidth: column.minWidth }}
                  sx={{
                    fontWeight: '600',
                    backgroundColor: 'primary.main',
                    color: 'white',
                    borderBottom: '2px solid rgba(255, 255, 255, 0.3)',
                    ...(column.hideOnMobile && {
                      display: { xs: 'none', md: 'table-cell' },
                    }),
                  }}
                  scope="col"
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} sx={{ p: 0 }}>
                  <LoadingSkeleton variant="table" rows={rowsPerPage} />
                </TableCell>
              </TableRow>
            ) : !data || data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} sx={{ p: 0 }}>
                  <EmptyState
                    variant="noData"
                    title={emptyMessage}
                    description="เริ่มต้นด้วยการสร้างข้อมูลใหม่"
                    action={onEmptyAction ? {
                      label: emptyActionLabel,
                      onClick: onEmptyAction,
                      icon: <AddIcon />,
                    } : undefined}
                  />
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, index) => {
                const rowId = getRowId(row, index);
                return (
                  <TableRow
                    hover
                    key={rowId}
                    onClick={() => onRowClick && onRowClick(row)}
                    sx={{
                      cursor: onRowClick ? 'pointer' : 'default',
                      '&:last-child td, &:last-child th': { border: 0 },
                    }}
                  >
                    {columns.map((column) => {
                      const value = row[column.id];
                      return (
                        <TableCell
                          key={column.id}
                          align={column.align || 'left'}
                          sx={column.hideOnMobile ? { display: { xs: 'none', md: 'table-cell' } } : undefined}
                        >
                          {column.format ? column.format(value, row) : value}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </MuiTable>
      </TableContainer>
      {(onPageChange || onRowsPerPageChange) && (
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="แถวต่อหน้า:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} จาก ${count !== -1 ? count : `มากกว่า ${to}`}`
          }
          aria-label={`หน้า ${page + 1} จากทั้งหมด ${Math.ceil(total / rowsPerPage)} หน้า`}
        />
      )}
    </Paper>
  );
}

export default Table;
